import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { collection, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FaArrowLeft, FaArrowRight, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

function StudentWithdrawals() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'withdrawalDate', direction: 'descending' });
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingEntry, setViewingEntry] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedClass, setSelectedClass] = useState('All');

  const classrooms = ['All', 'Cubs', 'Teddies', 'Koalas', 'Pandas', 'Preschool', 'Pre-K', 'Kindergarten'];

  useEffect(() => {
    const fetchStudentWithdrawals = async () => {
      try {
        const withdrawalsCollection = collection(db, 'studentWithdrawals');
        const withdrawalsSnapshot = await getDocs(withdrawalsCollection);
        const withdrawalsList = withdrawalsSnapshot.docs.map(doc => {
          const data = doc.data();
          const dateObj = data.withdrawalDate.toDate();
          return {
            id: doc.id,
            ...data,
            withdrawalDate: dateObj,
            formattedDate: dateObj.toLocaleDateString()
          };
        });

        withdrawalsList.sort((a, b) => b.withdrawalDate.getTime() - a.withdrawalDate.getTime());
        setWithdrawals(withdrawalsList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching student withdrawals: ", error);
        setLoading(false);
      }
    };

    fetchStudentWithdrawals();
  }, []);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleView = (entry) => {
    setViewingEntry(entry);
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
  };

  const handleSaveEdit = async (editedEntry) => {
    try {
      const entryRef = doc(db, 'studentWithdrawals', editedEntry.id);
  
      // Update Firestore document
      await updateDoc(entryRef, {
        ...editedEntry,
        withdrawalDate: editedEntry.withdrawalDate,
      });
  
      // Update the withdrawal list locally
      setWithdrawals(withdrawals.map(entry => 
        entry.id === editedEntry.id
          ? {
              ...editedEntry,
              formattedDate: editedEntry.withdrawalDate.toLocaleDateString(), // Reformat date for display
            }
          : entry
      ));
      
      setEditingEntry(null);
    } catch (error) {
      console.error("Error updating entry: ", error);
    }
  };
  

  const handleDelete = async (entryId) => {
    if (window.confirm('Are you sure you want to delete this withdrawal entry?')) {
      try {
        await deleteDoc(doc(db, 'studentWithdrawals', entryId));
        setWithdrawals(withdrawals.filter(entry => entry.id !== entryId));
      } catch (error) {
        console.error("Error deleting entry: ", error);
      }
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleEntriesPerPageChange = (event) => {
    setEntriesPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => (prev * entriesPerPage < filteredAndSortedEntries.length ? prev + 1 : prev));
  };

  const handleClassChange = (event) => {
    setSelectedClass(event.target.value);
    setCurrentPage(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />;
  };

  const filteredAndSortedEntries = withdrawals
    .filter(entry => 
      entry.studentName.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (selectedClass === 'All' || entry.classroom === selectedClass)
    )
    .sort((a, b) => {
      if (sortConfig.key === 'withdrawalDate') {
        return sortConfig.direction === 'ascending' 
          ? a.withdrawalDate.getTime() - b.withdrawalDate.getTime()
          : b.withdrawalDate.getTime() - a.withdrawalDate.getTime();
      } else if (sortConfig.key !== null) {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
      }
      return 0;
    });

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredAndSortedEntries.slice(indexOfFirstEntry, indexOfLastEntry);

  if (loading) {
    return <p className="text-center">Loading...</p>;
  }

  if (viewingEntry) {
    return (
      <div className="p-10 max-w-4xl mx-auto bg-white shadow-lg rounded-lg">
        <h2 className="text-4xl font-bold mb-8 text-blue-600">{viewingEntry.studentName}</h2>
        <div className="flex flex-col space-y-4 text-lg">
          <p className="flex items-center">
            <span className="font-semibold mr-2 text-gray-700 w-32">Withdrawal Date:</span> 
            <span className="bg-blue-100 px-3 py-1 rounded">{viewingEntry.formattedDate}</span>
          </p>
          <p className="flex items-center">
            <span className="font-semibold mr-2 text-gray-700 w-32">Class:</span> 
            <span className="bg-green-100 px-3 py-1 rounded">{viewingEntry.classroom}</span>
          </p>
          <p className="flex items-center">
            <span className="font-semibold mr-2 text-gray-700 w-32">Reason:</span> 
            <span className="bg-yellow-100 px-3 py-1 rounded">{viewingEntry.reason}</span>
          </p>
          <p className="flex items-center">
            <span className="font-semibold mr-2 text-gray-700 w-32">Notice:</span> 
            <span className="bg-purple-100 px-3 py-1 rounded">{viewingEntry.notice}</span>
          </p>
        </div>
        <div className="mt-8">
          <h3 className="text-2xl font-semibold mb-4 text-gray-700">Notes:</h3>
          <p className="bg-purple-100 px-3 py-1 rounded">{viewingEntry.notes || 'No notes available.'}</p>
        </div>
        <button 
          className="mt-8 px-6 py-3 bg-blue-500 text-white text-xl rounded-lg hover:bg-blue-600 transition duration-300 ease-in-out flex items-center"
          onClick={() => setViewingEntry(null)}
        >
          <FaArrowLeft className="mr-2" />
          Back to List
        </button>
      </div>
    );
  }

  if (editingEntry) {
    return (
      <div className="p-4">
        <h2 className="text-2xl font-bold mb-4">Edit Withdrawal Entry</h2>
        <form onSubmit={(e) => {
          e.preventDefault();
          handleSaveEdit(editingEntry);
        }}>
          <div className="mb-4">
            <label className="block mb-2">Student Name:</label>
            <input 
              type="text" 
              value={editingEntry.studentName} 
              onChange={(e) => setEditingEntry({...editingEntry, studentName: e.target.value})}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Class:</label>
            <select
              value={editingEntry.classroom}
              onChange={(e) => setEditingEntry({...editingEntry, classroom: e.target.value})}
              className="w-full px-3 py-2 border rounded"
            >
              {classrooms.filter(c => c !== 'All').map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block mb-2">Withdrawal Date:</label>
            <DatePicker
              selected={editingEntry.withdrawalDate}
              onChange={(date) => setEditingEntry({ ...editingEntry, withdrawalDate: date })}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div className="mb-4">
            <label className="block mb-2">Reason:</label>
            <select
              value={editingEntry.reason}
              onChange={(e) => setEditingEntry({...editingEntry, reason: e.target.value})}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="Changing Preschools">Changing Preschools</option>
              <option value="Moving">Moving</option>
              <option value="Financial">Financial</option>
              <option value="Going to Private School">Going to Private School</option>
              <option value="Other">Other</option>
              <option value="Issue With School">Issue With School</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block mb-2">Notice:</label>
            <select
              value={editingEntry.notice}
              onChange={(e) => setEditingEntry({...editingEntry, notice: e.target.value})}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="30 Day Notice">30 Day Notice</option>
              <option value="Waived">Waived</option>
              <option value="Other Notice">Other Notice</option>
              <option value="No Notice">No Notice</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block mb-2">Notes:</label>
            <textarea 
              value={editingEntry.notes} 
              onChange={(e) => setEditingEntry({...editingEntry, notes: e.target.value})}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          <div>
            <button type="submit" className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 mr-2">
              Save
            </button>
            <button type="button" onClick={() => setEditingEntry(null)} className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h1 className="text-3xl font-bold mb-4">Student Withdrawals</h1>
      <div className="mb-4 flex justify-between items-center">
        <div className="flex items-center">
          <label className="mr-2">Entries per page:</label>
          <select
            value={entriesPerPage}
            onChange={handleEntriesPerPageChange}
            className="px-2 py-1 border border-gray-300 rounded"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div className="flex items-center ml-4">
          <label className="mr-2">Class:</label>
          <select
            value={selectedClass}
            onChange={handleClassChange}
            className="px-2 py-1 border border-gray-300 rounded"
          >
            {classrooms.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center ml-4">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
            className="px-4 py-2 border border-gray-300 rounded"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border-gray-200 shadow-md rounded-lg table-fixed">
          <thead className="bg-gray-100 text-gray-800">
            <tr>
              <th className="w-12 py-2 px-4 border-b border-gray-200 text-center">#</th>
              <th className="w-1/6 py-2 px-4 border-b border-gray-200 text-center cursor-pointer" onClick={() => handleSort('studentName')}>
                <div className="flex items-center justify-center">
                  Name {getSortIcon('studentName')}
                </div>
              </th>
              <th className="w-1/6 py-2 px-4 border-b border-gray-200 text-center cursor-pointer" onClick={() => handleSort('classroom')}>
                <div className="flex items-center justify-center">
                  Class {getSortIcon('classroom')}
                </div>
              </th>
              <th className="w-1/6 py-2 px-4 border-b border-gray-200 text-center cursor-pointer" onClick={() => handleSort('withdrawalDate')}>
                <div className="flex items-center justify-center">
                  Date {getSortIcon('withdrawalDate')}
                </div>
              </th>
              <th className="w-1/6 py-2 px-4 border-b border-gray-200 text-center cursor-pointer" onClick={() => handleSort('notice')}>
                <div className="flex items-center justify-center">
                  Notice {getSortIcon('notice')}
                </div>
              </th>
              <th className="w-1/6 py-2 px-4 border-b border-gray-200 text-center cursor-pointer" onClick={() => handleSort('reason')}>
                <div className="flex items-center justify-center">
                  Reason {getSortIcon('reason')}
                </div>
              </th>
              <th className="w-1/6 py-2 px-4 border-b border-gray-200 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {currentEntries.map((entry, index) => (
              <tr key={entry.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{index + 1 + indexOfFirstEntry}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{entry.studentName}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{entry.classroom}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{entry.formattedDate}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{entry.notice}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">{entry.reason}</td>
                <td className="py-2 px-4 border-b border-gray-200 text-center">
                  <div className="flex justify-center space-x-2">
                    <button 
                      className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                      onClick={() => handleView(entry)}
                    >
                      View
                    </button>
                    <button 
                      className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                      onClick={() => handleEdit(entry)}
                    >
                      Edit
                    </button>
                    <button 
                      className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                      onClick={() => handleDelete(entry.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between mt-4">
        <div className="flex space-x-2">
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
          >
            <FaArrowLeft />
          </button>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={handleNextPage}
            disabled={indexOfLastEntry >= filteredAndSortedEntries.length}
          >
            <FaArrowRight />
          </button>
        </div>
        <div className="text-gray-600">
          Showing {indexOfFirstEntry + 1} to {Math.min(indexOfLastEntry, filteredAndSortedEntries.length)} of {filteredAndSortedEntries.length} entries
        </div>
      </div>
    </div>
  );
}

export default StudentWithdrawals;
